import { useQuery } from '@tanstack/react-query'
import { Activity, Briefcase, Clock, Radio, Target, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { useAuth } from '../auth'
import { AppLayout, PageHeader, SectionTitle } from '../components/layout/AppLayout'
import { Card, CardBody } from '../components/ui/Card'
import { Empty, Skeleton } from '../components/ui/Empty'
import { apiClient } from '../lib/api'
import { cn, formatHours } from '../lib/utils'

type Overview = {
  counts: { clients: number; activeProjects: number; activeLeads: number }
  weekTime: { totalHours: number; byDay: { label: string; hours: number; seconds: number }[] }
}

type StatTone = 'cyan' | 'violet' | 'emerald' | 'amber'

const toneStyles: Record<StatTone, { icon: string; bar: string; glow: string }> = {
  cyan: {
    icon: 'text-cyan-300',
    bar: 'from-cyan-500 to-cyan-400',
    glow: 'shadow-[0_0_24px_-6px_rgba(34,211,238,0.4)]',
  },
  violet: {
    icon: 'text-violet-300',
    bar: 'from-violet-500 to-violet-400',
    glow: 'shadow-[0_0_24px_-6px_rgba(167,139,250,0.4)]',
  },
  emerald: {
    icon: 'text-emerald-300',
    bar: 'from-emerald-500 to-emerald-400',
    glow: 'shadow-[0_0_24px_-6px_rgba(52,211,153,0.4)]',
  },
  amber: {
    icon: 'text-amber-300',
    bar: 'from-amber-500 to-amber-400',
    glow: 'shadow-[0_0_24px_-6px_rgba(251,191,36,0.4)]',
  },
}

export function DashboardPage() {
  const { user } = useAuth()
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => apiClient.get<Overview>('/dashboard/overview'),
  })

  const stats: { label: string; value: ReactNode; icon: typeof Users; tone: StatTone }[] = [
    { label: 'Clienti', value: data?.counts.clients, icon: Users, tone: 'cyan' },
    {
      label: 'Progetti attivi',
      value: data?.counts.activeProjects,
      icon: Briefcase,
      tone: 'violet',
    },
    { label: 'Lead totali', value: data?.counts.activeLeads, icon: Target, tone: 'amber' },
    {
      label: 'Ore (7 gg)',
      value: data ? formatHours(data.weekTime.totalHours) : undefined,
      icon: Clock,
      tone: 'emerald',
    },
  ]

  return (
    <AppLayout>
      <PageHeader
        title={`Bentornato, ${user?.name?.split(' ')[0] ?? 'pilota'}.`}
        description="Mission control · stato del tuo squadrone in tempo reale."
      />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <StatCard
            key={s.label}
            label={s.label}
            value={s.value}
            icon={s.icon}
            tone={s.tone}
            loading={isLoading}
          />
        ))}
      </section>

      <section className="mt-10 grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-12 -right-12 size-60 rounded-full bg-cyan-500/10 blur-3xl"
          />
          <CardBody>
            <SectionTitle
              title="Ore degli ultimi 7 giorni"
              description="Sessioni tracciate dal sistema TimeTrack"
              action={
                <span className="flex items-center gap-1.5 text-[11px] text-zinc-500 uppercase tracking-wider">
                  <Activity className="size-3.5 text-cyan-400" />
                  per giorno
                </span>
              }
            />
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : data && data.weekTime.byDay.length > 0 ? (
              <ul className="space-y-2.5">
                {data.weekTime.byDay
                  .slice()
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((row) => {
                    const max = Math.max(...data.weekTime.byDay.map((r) => r.seconds), 1)
                    const pct = Math.round((row.seconds / max) * 100)
                    return (
                      <li key={row.label} className="flex items-center gap-3 text-sm">
                        <span className="w-28 text-zinc-400 tabular-nums font-mono text-xs">
                          {row.label}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-zinc-900/80 overflow-hidden border border-zinc-800/60">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-500 via-cyan-400 to-violet-500 shadow-[0_0_10px_0_rgba(34,211,238,0.5)] transition-[width] duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-14 text-right tabular-nums text-zinc-200 font-mono text-xs">
                          {formatHours(row.hours)}
                        </span>
                      </li>
                    )
                  })}
              </ul>
            ) : (
              <Empty
                icon={Clock}
                title="Nessuna sessione registrata"
                description="Nessuna ora tracciata negli ultimi 7 giorni."
              />
            )}
          </CardBody>
        </Card>

        <Card className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-10 -left-10 size-48 rounded-full bg-violet-500/10 blur-3xl"
          />
          <CardBody>
            <SectionTitle
              title="Telemetria"
              description="Stato dei sottosistemi"
              action={<Radio className="size-3.5 text-cyan-400" />}
            />
            <ul className="space-y-2.5 text-sm">
              <Status name="API" online />
              <Status name="MCP server" online />
              <Status name="DB Postgres" online />
              <Status name="Auth" online />
              <Status name="TimeTrack hooks" online />
            </ul>
            <p className="mt-5 text-[11px] text-zinc-600 leading-relaxed">
              Verde = ultime query OK. Dettagli operativi nella pagina Setup.
            </p>
          </CardBody>
        </Card>
      </section>
    </AppLayout>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  loading,
}: {
  label: string
  value: ReactNode
  icon: typeof Users
  tone: StatTone
  loading: boolean
}) {
  const t = toneStyles[tone]
  return (
    <Card className={cn('relative overflow-hidden group transition-shadow duration-200', t.glow)}>
      <CardBody>
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-medium">
            {label}
          </div>
          <Icon className={cn('size-4 transition-transform group-hover:scale-110', t.icon)} />
        </div>
        <div className="mt-3 font-display text-3xl font-semibold text-zinc-50 tabular-nums tracking-tight">
          {loading ? <Skeleton className="h-8 w-20" /> : (value ?? '—')}
        </div>
        <div className="mt-4 h-px overflow-hidden rounded-full bg-zinc-800/60">
          <div className={cn('h-full w-1/2 bg-gradient-to-r', t.bar)} />
        </div>
      </CardBody>
    </Card>
  )
}

function Status({ name, online }: { name: string; online: boolean }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-zinc-300">{name}</span>
      <span
        className={cn(
          'inline-flex items-center gap-2 text-xs font-medium',
          online ? 'text-emerald-300' : 'text-zinc-500',
        )}
      >
        <span
          className={cn(
            'size-1.5 rounded-full',
            online ? 'bg-emerald-400 shadow-[0_0_8px_0_rgba(52,211,153,0.7)]' : 'bg-zinc-600',
          )}
        />
        {online ? 'online' : 'offline'}
      </span>
    </li>
  )
}
